import React from "react";
import { Bell, ChevronDown, ChevronRight, Eye, Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/api/manufacturing";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function formatDate(value) {
  return value ? new Date(`${value}T00:00:00`).toLocaleDateString("es-ES") : "-";
}

export function ProductionOrderTree({ orders, loading, selectedOrderId, deletingId, onOpen, onDelete }) {
  const [expandedIds, setExpandedIds] = React.useState(new Set());
  const toggle = (id) => setExpandedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const row = (order, product = null, nested = false) => {
    const grouped = !product;
    const tracking = product || order;
    const complete = tracking.manufacturing_complete && Number(tracking.pending_invoice_count) === 0 && !tracking.has_incident;
    const tone = complete ? 'bg-green-50' : 'bg-white';
    const expanded = expandedIds.has(order.id);
    return (
      <tr
        key={product?.id || order.id}
        className={`${tone} ${selectedOrderId === order.id ? "outline outline-1 outline-blue-300" : ""} ${product ? "cursor-pointer hover:brightness-95" : ""}`}
        onClick={product ? (event) => {
          if (event.target.closest("button, a, input, select, textarea")) return;
          onOpen(order, "edit", product.id);
        } : undefined}
      >
        <td className={`border px-3 py-2 font-semibold whitespace-nowrap ${nested ? "pl-10" : ""}`}>
          {grouped ? (
            <button type="button" className="flex items-center gap-2 rounded focus-visible:outline focus-visible:outline-2" aria-expanded={expanded} aria-label={`${expanded ? "Contraer" : "Desplegar"} ${order.number}`} onClick={() => toggle(order.id)}>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{order.number}
            </button>
          ) : <span title={`OF ${order.number}`}>{product.subnumber}</span>}
        </td>
        <td className="border px-3 py-2">
          {grouped ? <>{order.description || "Orden de fabricación"}<span className="ml-2 text-xs text-gray-500">{order.matching_count < order.product_count ? `${order.matching_count} de ${order.product_count} S/O coinciden · Resumen de toda la OF` : `${order.product_count} S/O`}</span></> : `${product.product_code_snapshot} · ${product.description_snapshot}`}
        </td>
        <td className="border px-3 py-2 whitespace-nowrap">{formatDate(grouped ? order.first_order_date : product.order_date)}</td>
        <td className="border px-3 py-2 whitespace-nowrap">{formatDate(tracking.final_received_date || (grouped ? order.last_finished_date : product.finished_date))}</td>
        <td className="border px-3 py-2">{order.family?.descrip || "-"}</td>
        <td className="border px-3 py-2">{order.season?.descrip || "-"}</td>
        <td className="border px-3 py-2 text-right">{grouped ? order.total_quantity : product.total_quantity}</td>
        <td className="border px-3 py-2 text-right">{tracking.final_quantity ?? '—'}</td>
        <td className="border px-3 py-2 text-right whitespace-nowrap">{formatMoney(grouped ? order.grand_total : product.total_cost)}</td>
        <td className="border px-3 py-2">
          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
            <Popover><PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm" className={complete ? 'border-green-300 bg-green-100 text-green-900' : tracking.has_incident ? 'border-red-300 bg-red-100 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-900'} aria-label={`Incidencia · ${product?.subnumber || order.number}`}>Incidencia</Button>
            </PopoverTrigger><PopoverContent className="w-80 text-sm" align="end" onClick={(event) => event.stopPropagation()}>
              <p className="mb-2 font-semibold">Cierre de {product?.subnumber || order.number}: {complete ? 'Terminada' : 'Pendiente'}</p>
              {grouped && <p className="mb-2 text-xs text-gray-500">Resumen de todas las S/O de la OF.</p>}
              <ul className="space-y-2">
                <li>Último proceso: <b>{tracking.final_quantity ?? 'Sin registrar'}</b> prendas recibidas.</li>
                <li>Fecha de recepción: <b>{formatDate(tracking.final_received_date)}</b>{grouped && !tracking.manufacturing_complete ? ' · Hay S/O pendientes de recepción' : ''}</li>
                <li>Facturas: <b>{tracking.pending_invoice_count > 0 ? `${tracking.pending_invoice_count} pendientes` : 'Todas creadas'}</b>.</li>
                <li>Matrices: <b className={tracking.has_incident ? 'text-red-700' : 'text-green-700'}>{tracking.has_incident ? 'Diferencias pendientes de cuadrar' : 'Sin incidencias'}</b>.</li>
              </ul>
            </PopoverContent></Popover>
            <Button type="button" variant="outline" size="sm" className={tracking.pending_document_count > 0 ? 'border-amber-300 bg-amber-50 text-amber-900' : ''}
              title={`${tracking.pending_document_count || 0} documentos pendientes de validar e incorporar`} aria-label={`Documentos · ${product?.subnumber || order.number}: ${tracking.pending_document_count || 0} pendientes`}
              onClick={() => onOpen(order, "edit", product?.id || null)}><Bell className="h-4 w-4" />{tracking.pending_document_count > 0 && <span className="ml-1">{tracking.pending_document_count}</span>}</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpen(order, "view", product?.id || null)} title="Consultar" aria-label={`Consultar ${product?.subnumber || order.number}`}><Eye className="h-4 w-4" /></Button>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpen(order, "edit", product?.id || null)} title="Editar" aria-label={`Editar ${product?.subnumber || order.number}`}><Pencil className="h-4 w-4" /></Button>
            {!nested && <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(order)} disabled={deletingId === order.id} title={`Eliminar OF ${order.number}`} aria-label={`Eliminar OF ${order.number}`}>{deletingId === order.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}</Button>}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="overflow-x-auto" aria-busy={loading}>
      <table className="w-full min-w-[1150px] border-collapse text-sm">
        <caption className="sr-only">Órdenes de fabricación agrupadas por OF y ordenadas por su S/O más alta</caption>
        <thead className="bg-gray-50 text-left"><tr>
          {["S/O · OF", "Artículo / descripción", "Creación", "Finalización", "Familia", "Temporada", "Prendas", "Entrega final", "Coste", "Acciones"].map((label) => <th key={label} scope="col" className="border px-3 py-2">{label}</th>)}
        </tr></thead>
        <tbody>
          {orders.map((order) => order.product_count > 1 ? (
            <React.Fragment key={order.id}>
              {row(order)}
              {expandedIds.has(order.id) && order.products.map((product) => row(order, product, true))}
            </React.Fragment>
          ) : order.products.map((product) => row(order, product)))}
          {!orders.length && <tr><td colSpan={10} className="border px-3 py-6 text-center text-gray-500">{loading ? "Cargando órdenes..." : "No hay órdenes que coincidan con los filtros."}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
